import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';
import { ReactComponent as CheckIcon } from '@repo/common/assets/icons/20px/icon_20px_check.svg';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/20px/icon_20px_cancel.svg';
import {
  BaseButton,
  getJobOptionCode,
  getJobOptionLabel,
  useAuth,
  useLang,
  useT,
} from '@repo/common';
import { getAllCareers } from '../../../../api/career/career.api';

/* ────────────────────────── helpers ────────────────────────── */

const toArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];

const toProfile = (resume) => {
  if (!resume?.profile) return {};
  if (Array.isArray(resume.profile)) return resume.profile[0] || {};
  return resume.profile;
};

const toText = (value) => {
  if (Array.isArray(value)) return toText(value[0]);
  if (value == null) return '';
  return String(value).trim();
};

const normalize = (value) => toText(value).toLowerCase();

// 비교용: 소문자 + 영문/숫자/한글만 남김 ("United States" → "unitedstates")
const compact = (value) => normalize(value).replace(/[^a-z0-9가-힣]/g, '');

/* ── Location ── */
const KOREA_LOCATION_KEYWORDS = [
  'korea',
  'seoul',
  'busan',
  'incheon',
  'daegu',
  'daejeon',
  'gwangju',
  'ulsan',
  'sejong',
  'gyeonggi',
  'gangwon',
  'chungcheong',
  'chungbuk',
  'chungnam',
  'jeolla',
  'jeonbuk',
  'jeonnam',
  'gyeongsang',
  'gyeongbuk',
  'gyeongnam',
  'jeju',
  'suwon',
  'seongnam',
  'yongin',
  'goyang',
  'bucheon',
  'ansan',
  'anyang',
  'hwaseong',
  'cheongju',
  'cheonan',
  'jeonju',
  'changwon',
  'pohang',
  'gimhae',
  '한국',
  '대한민국',
  '서울',
  '부산',
  '인천',
  '대구',
  '대전',
  '광주',
  '울산',
  '세종',
  '경기',
  '강원',
  '충청',
  '충북',
  '충남',
  '전라',
  '전북',
  '전남',
  '경상',
  '경북',
  '경남',
  '제주',
];

const isKoreaLocation = (location) => {
  const value = normalize(location);
  if (!value) return false;
  // "North Korea" 는 제외
  if (value.includes('north korea') || value.includes('dprk')) return false;
  return KOREA_LOCATION_KEYWORDS.some((keyword) => value.includes(keyword));
};

/* ── Career ── */
// 이력서 career 값: 'Entry' | '1 yrs' … '8+ yrs' (CareerList). 자유 입력도 방어.
const parseCareerYears = (careerText) => {
  const text = normalize(careerText);
  if (!text) return null;
  if (/entry|신입|fresh|new ?grad/.test(text)) return 0;

  // "2019 ~ 2023" 같은 연도 표기는 연차로 오인하지 않는다
  const nums = (text.match(/\d+(?:\.\d+)?/g) || [])
    .map(Number)
    .filter((n) => n < 60);
  if (!nums.length) return null;
  // "3~5 yrs" 처럼 범위면 최소값 기준
  return Math.min(...nums);
};

// CAREER_LEVEL 옵션 코드 → 최소 연차. ANY/미설정은 null(=조건 없음)
const getMinYearsForCareerCode = (code) => {
  switch ((code || '').toUpperCase()) {
    case 'ENTRY':
    case 'JUNIOR':
      return 0;
    case 'JUNIOR_MID':
      return 1;
    case 'MID':
      return 3;
    case 'SENIOR':
      return 5;
    case 'LEAD':
      return 8;
    default:
      return null;
  }
};

/* ── Korean skill ── */
// KoreanSkillsList: Beginner | Intermediate | Advanced | Native | TOPIK certified | TOPIK Speaking certified
// profile.topik: { type: 'topik' | 'topikSpeaking', level: 'TOPIK Level 4 (4급)', date }
const parseTopikLevel = (value) => {
  const text = toText(value);
  const m = text.match(/(\d)\s*(급|$)|level\s*(\d)|(\d)/i);
  if (!m) return null;
  const n = Number(m[1] ?? m[3] ?? m[4]);
  return n >= 1 && n <= 6 ? n : null;
};

const topikLevelToRank = (level) => {
  if (level == null) return null;
  if (level >= 5) return 3; // Advanced
  if (level >= 3) return 2; // Intermediate
  return 1; // Beginner
};

const getUserKoreanRank = (koreanSkills, topik) => {
  const text = normalize(koreanSkills);
  const topikLevel =
    topik?.type === 'topik' ? parseTopikLevel(topik?.level) : null;
  const topikRank = topikLevelToRank(topikLevel);

  let textRank = null;
  if (text.includes('native') || text.includes('원어민')) textRank = 4;
  else if (text.includes('advanced') || text.includes('고급')) textRank = 3;
  else if (text.includes('intermediate') || text.includes('중급'))
    textRank = 2;
  else if (text.includes('beginner') || text.includes('초급')) textRank = 1;
  else if (text.includes('topik')) textRank = topikRank ?? 2; // 급수 미기재 TOPIK → 중급 가정

  if (textRank == null && topikRank == null) return null;
  return Math.max(textRank ?? 0, topikRank ?? 0);
};

const getRequiredKoreanRank = (code) => {
  switch ((code || '').toUpperCase()) {
    case 'NOT_REQUIRED':
      return 0;
    case 'BEGINNER':
      return 1;
    case 'INTERMEDIATE':
      return 2;
    case 'ADVANCED':
      return 3;
    case 'NATIVE':
      return 4;
    default:
      return null;
  }
};

/* ── TOPIK requirement ── */
// TOPIK_REQUIREMENT: NOT_REQUIRED | PREFERRED | REQUIRED | MIN_LEVEL(+TOPIK_LEVEL)
const getRequiredTopikLevel = (code, levelCode) => {
  const c = (code || '').toUpperCase();
  if (c === 'REQUIRED') return 1;
  if (c === 'MIN_LEVEL') {
    const m = (levelCode || '').match(/(\d)/);
    return m ? Number(m[1]) : 1;
  }
  return null; // NOT_REQUIRED / PREFERRED / 미설정 → 필수 조건 아님
};

/* ── Visa ── */
// 이력서 visa[].visaType: 'E-7' | 'D-10' | 'No'(No Visa) …  공고 옵션 코드: D2 | D10 | E7(E-1~E-7) | E8_E10 | F1_F3 | F2 … | ANY
const VISA_CODE_MATCHERS = {
  D2: ['D-2'],
  D4: ['D-4'],
  D10: ['D-10'],
  E7: ['E-1', 'E-2', 'E-3', 'E-4', 'E-5', 'E-6', 'E-7'],
  E8_E10: ['E-8', 'E-9', 'E-10'],
  F1_F3: ['F-1', 'F-3'],
  F2: ['F-2'],
  F4: ['F-4'],
  F5: ['F-5'],
  F6: ['F-6'],
  H1: ['H-1'],
  H2: ['H-2'],
};

const normalizeVisaCode = (value) => {
  const m = toText(value)
    .toUpperCase()
    .match(/([A-Z])\s*-?\s*(\d{1,2})/);
  return m ? `${m[1]}-${Number(m[2])}` : '';
};

const getUserVisaCodes = (visaList) =>
  toArray(visaList)
    .map((v) => normalizeVisaCode(v?.visaType))
    .filter(Boolean);

const isVisaCodeMatch = (jobCode, userVisaCode) => {
  const matchers = VISA_CODE_MATCHERS[(jobCode || '').toUpperCase()];
  if (!matchers) return false;
  return matchers.includes(userVisaCode);
};

/* ── Language ── */
const LANGUAGE_ALIASES = {
  ENGLISH: ['english', '영어'],
  JAPANESE: ['japanese', '일본어'],
  CHINESE: ['chinese', 'mandarin', '중국어'],
  KOREAN: ['korean', '한국어'],
};

const isLanguageMatch = (code, labelText, userLanguages) => {
  const aliases = LANGUAGE_ALIASES[(code || '').toUpperCase()] || [
    compact(labelText),
  ];
  return toArray(userLanguages).some((item) => {
    const name = compact(item?.language ?? item);
    return (
      name &&
      aliases.some((alias) => {
        const a = compact(alias);
        return name === a || name.includes(a) || a.includes(name);
      })
    );
  });
};

/* ── Nationality ── */
const NATIONALITY_ALIASES = {
  JAPAN: ['japan', 'japanese', 'jp', 'jpn', '일본'],
  USA: [
    'usa',
    'us',
    'u.s.',
    'united states',
    'unitedstates',
    'america',
    'american',
    '미국',
  ],
  CHINA: ['china', 'chinese', 'cn', 'chn', 'prc', '중국'],
};

const isNationalityMatch = (requiredCode, requiredText, userNationality) => {
  const code = (requiredCode || '').toUpperCase();
  const required = compact(requiredText);
  const user = compact(userNationality);

  if (!code && !required) return true;
  if (
    required.includes('nopreference') ||
    required.includes('any') ||
    required.includes('무관')
  )
    return true;
  if (!user) return false;

  const aliases = NATIONALITY_ALIASES[code];
  if (aliases) {
    return aliases.some((alias) => {
      const a = compact(alias);
      return a.length <= 3 ? user === a : user.includes(a) || a.includes(user);
    });
  }

  // OTHER(직접 입력): 콤마/슬래시로 여러 국가를 적은 경우도 처리
  return toText(requiredText)
    .split(/[,/·]/)
    .map(compact)
    .filter(Boolean)
    .some((r) => user.includes(r) || r.includes(user));
};

/* ────────────────────────── component ────────────────────────── */

const DetailSidebarRequirements = ({ job, optionMeta }) => {
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useLang();
  const [isOpen, setIsOpen] = useState(false);
  const [isCareerLoading, setIsCareerLoading] = useState(false);
  const [selectedResume, setSelectedResume] = useState(null);
  const [profile, setProfile] = useState({});
  const { user } = useAuth();
  const isLogin = Boolean(user);
  const notLoginImage = `${process.env.REACT_APP_ASSETS_URL}/JobBoard/img-jobboard-not-login.svg`;
  const hasResume = Boolean(selectedResume);

  useEffect(() => {
    let isMounted = true;

    if (!isLogin) {
      setSelectedResume(null);
      setProfile({});
      return () => {
        isMounted = false;
      };
    }

    const fetchResume = async () => {
      try {
        setIsCareerLoading(true);
        const careers = await getAllCareers();
        if (!isMounted) return;

        const list = Array.isArray(careers) ? careers : [];
        const resume = list.find((item) => item?.standard) || list[0] || null;
        setSelectedResume(resume);
        setProfile(toProfile(resume));
      } catch (error) {
        if (!isMounted) return;
        console.error(
          'Failed to fetch careers for job requirement match:',
          error,
        );
        setSelectedResume(null);
        setProfile({});
      } finally {
        if (isMounted) setIsCareerLoading(false);
      }
    };

    fetchResume();

    return () => {
      isMounted = false;
    };
  }, [isLogin]);

  const sizeDownCancelIcon = (
    <CancelIcon className='w-[18px] h-[18px] shrink-0 text-icon-disable p-0.5' />
  );

  const notSet = t('Not set in resume', '이력서에 없음');
  const notSpecified = t('Not specified', '미지정');
  const label = (id, fallback) =>
    getJobOptionLabel(optionMeta, id, fallback, lang);

  const requirementRows = useMemo(() => {
    const userLocation = toText(profile?.location);
    const userKoreanSkill = toText(profile?.koreanSkills);
    const userTopik = profile?.topik;
    const userNationality = toText(
      toArray(profile?.nationality)[0] ?? profile?.nationality,
    );
    const userCareer = toText(profile?.career);
    const userVisaList = toArray(selectedResume?.visa);
    const userLanguages = toArray(selectedResume?.language);

    // status: 'match' | 'mismatch' | 'neutral'(판정 제외) | 'notSet'(이력서 미설정, 판정 제외)

    /* 1. Location */
    // 오버뷰의 Location 행과 같은 문구 (Korea residents only / Overseas applicants accepted)
    const locationRequirement = job?.koreaResidentsOnly
      ? t('Korea residents only', '국내 거주자만')
      : t('Overseas applicants accepted', '해외 거주자 지원 가능');
    let locationStatus = 'match'; // Overseas OK → 항상 충족
    if (job?.koreaResidentsOnly) {
      if (!userLocation) locationStatus = 'notSet';
      else locationStatus = isKoreaLocation(userLocation) ? 'match' : 'mismatch';
    }

    /* 2. Visa */
    const jobVisaIds = toArray(job?.visaTypeOptionItemIds);
    const jobVisaCodes = jobVisaIds.map((id) => getJobOptionCode(optionMeta, id));
    const jobVisaLabels = jobVisaIds
      .map((id) => label(id, ''))
      .filter(Boolean);
    const sponsorship = Boolean(job?.visaSponsorship);
    const anyVisaOk = jobVisaCodes.length === 0 || jobVisaCodes.includes('ANY');
    const userVisaCodes = getUserVisaCodes(userVisaList);
    const hasNoVisaEntry = userVisaList.some((v) =>
      /^no/i.test(toText(v?.visaType)),
    );
    const visaIncluded = userVisaCodes.some((userCode) =>
      jobVisaCodes.some((jobCode) => isVisaCodeMatch(jobCode, userCode)),
    );
    let visaStatus;
    if (anyVisaOk || visaIncluded) visaStatus = 'match';
    else if (sponsorship) visaStatus = 'neutral';
    else visaStatus = 'mismatch';
    const visaRequirement = anyVisaOk
      ? t('Any visa', '비자 무관')
      : jobVisaLabels.join(', ');
    const userVisaText = userVisaCodes.length
      ? userVisaCodes.join(', ')
      : hasNoVisaEntry
        ? t('No current Korean visa', '현재 한국 비자 없음')
        : '';

    /* 3. Korean skills (+ TOPIK) */
    const koreanRequirement = label(
      job?.koreanSkillLevelOptionItemId,
      notSpecified,
    );
    const koreanRequiredRank = getRequiredKoreanRank(
      getJobOptionCode(optionMeta, job?.koreanSkillLevelOptionItemId),
    );
    const userKoreanRank = getUserKoreanRank(userKoreanSkill, userTopik);
    const koreanMatched =
      koreanRequiredRank == null || koreanRequiredRank === 0
        ? true
        : userKoreanRank != null && userKoreanRank >= koreanRequiredRank;
    const userKoreanText = [
      userKoreanSkill,
      userTopik?.type === 'topik' && userTopik?.level ? userTopik.level : '',
    ]
      .filter(Boolean)
      .join(' · ');

    const topikCode = getJobOptionCode(
      optionMeta,
      job?.topikRequirementOptionItemId,
    );
    const topikLevelCode = getJobOptionCode(
      optionMeta,
      job?.topikRequirementLevelOptionItemId,
    );
    const requiredTopikLevel = getRequiredTopikLevel(topikCode, topikLevelCode);
    const userTopikLevel =
      userTopik?.type === 'topik' ? parseTopikLevel(userTopik?.level) : null;
    const hasTopikRequirement = requiredTopikLevel != null;
    const topikRequirement =
      topikCode === 'MIN_LEVEL'
        ? t(
            `TOPIK Level ${requiredTopikLevel}+`,
            `TOPIK ${requiredTopikLevel}급 이상`,
          )
        : t('TOPIK required', 'TOPIK 필수');
    const topikMatched = hasTopikRequirement
      ? userTopikLevel != null && userTopikLevel >= requiredTopikLevel
      : true;
    const koreanRowRequirement = [
      koreanRequirement,
      hasTopikRequirement ? topikRequirement : '',
    ]
      .filter(Boolean)
      .join(' · ');

    /* 4. Language skills — 요구 언어 없으면 행 숨김 */
    const requiredLanguages = toArray(job?.requiredLanguages)
      .map((item) => {
        const code = getJobOptionCode(optionMeta, item?.optionItemId);
        const optionLabel = label(item?.optionItemId, '');
        const name =
          code === 'OTHER'
            ? toText(item?.customText) || optionLabel
            : optionLabel || toText(item?.customText);
        return { code, name };
      })
      .filter((item) => item.name && item.code !== 'KOREAN'); // 한국어는 위 행에서 판정
    const languageResults = requiredLanguages.map((item) => ({
      ...item,
      matched: isLanguageMatch(item.code, item.name, userLanguages),
    }));
    const languageMatched = languageResults.every((item) => item.matched);
    const languageRequirement = languageResults
      .map((item) => `${item.name} ${item.matched ? '✓' : '✕'}`)
      .join(' · ');
    const userLanguageText = userLanguages
      .map((item) =>
        [toText(item?.language), toText(item?.proficiencyLevel)]
          .filter(Boolean)
          .join(' '),
      )
      .filter(Boolean)
      .join(', ');

    /* 5. Nationality */
    const nationalityCode = getJobOptionCode(
      optionMeta,
      job?.nationalityOptionItemId,
    );
    const nationalityLabel = label(job?.nationalityOptionItemId, '');
    const nationalityRequirement =
      nationalityCode === 'OTHER'
        ? toText(job?.nationalityCustomText) ||
          t('No preference', '국적 무관')
        : nationalityLabel ||
          toText(job?.nationalityCustomText) ||
          t('No preference', '국적 무관');
    const isPresetNationality = nationalityCode && nationalityCode !== 'OTHER';
    const nationalityMatched = isNationalityMatch(
      isPresetNationality ? nationalityCode : '',
      isPresetNationality ? '' : toText(job?.nationalityCustomText),
      userNationality,
    );

    /* 6. Career level */
    const careerCode = getJobOptionCode(
      optionMeta,
      job?.careerLevelOptionItemId,
    );
    const careerRequirement = label(job?.careerLevelOptionItemId, notSpecified);
    const minCareerYears = getMinYearsForCareerCode(careerCode); // ANY → null → 항상 충족
    const userCareerYears = parseCareerYears(userCareer);
    const careerMatched =
      minCareerYears == null
        ? true
        : userCareerYears != null && userCareerYears >= minCareerYears;

    const toStatus = (matched) => (matched ? 'match' : 'mismatch');

    const rows = [
      {
        key: 'location',
        label: t('Location', '거주지'),
        requirementValue: locationRequirement,
        resumeValue: userLocation || notSet,
        status: locationStatus,
      },
      {
        key: 'visa',
        label: t('Visa', '비자'),
        requirementValue: visaRequirement,
        resumeValue: userVisaText || notSet,
        status: visaStatus,
        neutralText: t('Sponsorship available', '스폰서십 가능'),
      },
      {
        key: 'korean',
        label: t('Korean skills', '한국어'),
        requirementValue: koreanRowRequirement,
        resumeValue: userKoreanText || notSet,
        status: toStatus(koreanMatched && topikMatched),
      },
    ];

    if (languageResults.length) {
      rows.push({
        key: 'language',
        label: t('Language skills', '언어'),
        requirementValue: languageRequirement,
        resumeValue: userLanguageText || notSet,
        status: toStatus(languageMatched),
      });
    }

    rows.push(
      {
        key: 'nationality',
        label: t('Nationality', '국적'),
        requirementValue: nationalityRequirement,
        resumeValue: userNationality || notSet,
        status: toStatus(nationalityMatched),
      },
      {
        key: 'career',
        label: t('Career level', '경력'),
        requirementValue: careerRequirement,
        resumeValue: userCareer || notSet,
        status: toStatus(careerMatched),
      },
    );

    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, optionMeta, profile, selectedResume, lang]);

  // 중립("Sponsorship available") · 미설정("Add to resume") 행은 분모에서 제외
  const scoredRows = requirementRows.filter(
    ({ status }) => status === 'match' || status === 'mismatch',
  );
  const totalCount = scoredRows.length;
  const matchedCount = scoredRows.filter(
    ({ status }) => status === 'match',
  ).length;

  const requirementHeadline =
    totalCount === 0
      ? t('Requirement summary', '요건 요약')
      : matchedCount === totalCount
      ? t(
          `You match all ${totalCount} key requirements.`,
          `핵심 요건 ${totalCount}개를 모두 충족해요.`,
        )
      : matchedCount === 0
        ? t(
            'No matches yet — start with Pathfinder.',
            '아직 충족한 요건이 없어요 — Pathfinder부터 시작해 보세요.',
          )
        : t(
            `You match ${matchedCount} of ${totalCount} key requirements.`,
            `핵심 요건 ${totalCount}개 중 ${matchedCount}개를 충족해요.`,
          );

  const mismatchedLabels = requirementRows
    .filter(({ status }) => status === 'mismatch')
    .map(({ label: rowLabel }) => rowLabel);
  const requirementDetail = mismatchedLabels.length
    ? t(
        `${mismatchedLabels.join(', ')} mismatch`,
        `${mismatchedLabels.join(', ')} 불일치`,
      )
    : t(
        'Your resume matches every key requirement.',
        '이력서가 모든 핵심 요건과 일치해요.',
      );

  return (
    <div className='flex flex-col items-start p-0 bg-grayscale-gray00 rounded-lg w-full'>
      <div className='flex flex-col items-start p-4 gap-0.5 border-b border-grayscale-gray02 w-full'>
        <div className='typo-b16Strong'>
          {!isLogin
            ? t('Match with this job', '이 공고와의 매칭')
            : hasResume && !isCareerLoading
              ? requirementHeadline
              : t('Requirement summary', '요건 요약')}
        </div>
        <div className='typo-b12 text-text-secondary'>
          {isLogin ? (
            hasResume && !isCareerLoading ? (
              requirementDetail
            ) : (
              t(
                'Compare this job requirements with your profile.',
                '공고 요건을 내 프로필과 비교해 보세요.',
              )
            )
          ) : (
            <div>
              <BaseButton
                type='text'
                size='s'
                hideIcon
                typo={false}
                isStatic
                onClick={() => navigate('/login')}
                className='h-auto min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 align-baseline text-text-primary underline'
                textWrapperClass='h-auto'
              >
                {t('Login', '로그인')}
              </BaseButton>{' '}
              {t('to view your match summary', '하고 매칭 결과를 확인하세요')}
            </div>
          )}
        </div>
      </div>
      <div
        onClick={() => setIsOpen((prev) => !prev)}
        className='flex items-center justify-between p-4 typo-b13Strong text-text-primary w-full cursor-pointer'
      >
        {t('Compare requirements', '요건 비교')}
        {isOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
      </div>

      <div
        className={`
            overflow-hidden transition-all duration-300 ease-in-out w-full
            ${isOpen ? 'max-h-[900px] opacity-100' : 'max-h-0 opacity-0'}
        `}
      >
        {isLogin ? (
          <div className='p-4 pt-0 w-full'>
            {isCareerLoading ? (
              <div className='typo-b12 text-text-secondary pb-2'>
                {t('Loading your resume...', '이력서를 불러오는 중...')}
              </div>
            ) : hasResume ? (
              <>
                <div className='pb-2'>
                  {requirementRows.map(
                    ({
                      key,
                      label: rowLabel,
                      requirementValue,
                      resumeValue,
                      status,
                      neutralText,
                    }) => (
                      <div key={key} className='flex flex-row items-start pb-2'>
                        <div className='typo-b12Strong text-text-primary w-[80px] shrink-0 pt-0.5'>
                          {rowLabel}
                        </div>
                        <div className='flex min-w-0 flex-1 items-start pb-0.5 gap-1'>
                          {status === 'match' ? (
                            <CheckIcon className='shrink-0 text-icon-success' />
                          ) : status === 'mismatch' ? (
                            sizeDownCancelIcon
                          ) : (
                            <span className='w-5 shrink-0 text-center typo-b12 text-text-secondary'>
                              –
                            </span>
                          )}
                          <div className='pt-0.5 min-w-0'>
                            <div className='typo-b12 text-text-primary'>
                              {requirementValue || '-'}
                            </div>
                            {status === 'neutral' ? (
                              <div className='typo-b11 text-text-secondary'>
                                {neutralText}
                                {resumeValue && resumeValue !== notSet
                                  ? ` · ${t('Your resume', '내 이력서')}: ${resumeValue}`
                                  : ''}
                              </div>
                            ) : status === 'notSet' ? (
                              <div className='typo-b11 text-text-secondary'>
                                {t('Your resume', '내 이력서')}: {notSet}{' '}
                                <BaseButton
                                  type='text'
                                  size='s'
                                  hideIcon
                                  typo={false}
                                  isStatic
                                  onClick={() => navigate('/career')}
                                  className='h-auto min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 typo-b11Strong text-text-primary underline'
                                  textWrapperClass='h-auto'
                                >
                                  {t('Add to resume', '이력서에 추가')}
                                </BaseButton>
                              </div>
                            ) : (
                              <div className='typo-b11 text-text-secondary'>
                                {t('Your resume', '내 이력서')}: {resumeValue}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ),
                  )}
                </div>
                <div className='typo-b11 bg-grayscale-gray01 text-text-secondary flex items-start px-2 py-1 rounded-lg'>
                  {t(
                    'This compares the employer requirements with your resume',
                    '기업의 요건을 내 이력서와 비교한 결과예요',
                  )}
                  {selectedResume?.title ? ` (${selectedResume.title})` : ''}.
                </div>
              </>
            ) : (
              <div className='flex flex-col items-start gap-2 pb-2'>
                <div className='typo-b12 text-text-secondary'>
                  {t(
                    'No saved resume found. Create a resume to compare requirements.',
                    '저장된 이력서가 없어요. 이력서를 만들면 요건을 비교할 수 있어요.',
                  )}
                </div>
                <BaseButton
                  type='text'
                  size='s'
                  hideIcon
                  typo={false}
                  isStatic
                  onClick={() => navigate('/career')}
                  className='h-auto min-w-0 cursor-pointer rounded-none border-0 bg-transparent p-0 typo-b12Strong text-text-primary underline'
                  textWrapperClass='h-auto'
                >
                  {t('Create resume', '이력서 만들기')}
                </BaseButton>
              </div>
            )}
          </div>
        ) : (
          <div className='flex flex-col justify-center items-center h-[140px] p-4 pt-0'>
            <img
              src={notLoginImage}
              alt='jobboard not found'
              className='pb-2'
            />
            <div className='typo-b12 text-text-placeholder text-center pb-2'>
              <div>{t('Login to compare your', '로그인하고 내 프로필을')}</div>
              <div>{t('profile with this job', '이 공고와 비교해 보세요')}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailSidebarRequirements;
